import { isNil, times, uniq } from 'lodash';
import moment, { Moment } from 'moment';
import { TimeAllocationPurpose } from '../../../../graphQL';
import { FriendlyAllocation } from '../../util';
import { AllocationType } from './types';
import { TimeZone } from '../../../../utils';
import { TimeType } from './TimeSelect';
import { LocalAllocation } from './Hooks/types';

// time options increment by this value
const TIME_INTERVAL = 15;
const SLOTS_PER_DAY = Math.floor((60 * 24) / TIME_INTERVAL);
// Number of next-day slots to generate (12:00 AM to 3:00 AM = 12 slots at 15-min intervals)
const NEXT_DAY_SLOTS = Math.floor((3 * 60 + TIME_INTERVAL) / TIME_INTERVAL);

export const getMsBetween = (startTime: Moment, endTime: Moment) => {
  return endTime.diff(startTime, 'milliseconds');
};

export const allocationTypeLabels: Record<AllocationType, string> = {
  default: 'Default',
  admin: 'Admin',
  intake: 'Intake Only',
  checkin: 'Follow-Up Only',
  timeOff: 'Time Off',
};

export const formatAllocation = (args: Omit<FriendlyAllocation, 'id'>) => {
  // format intake/checkin appt types as a list
  // other types are just for display and the purpose arg (below)
  const getAppointmentTypes = () => {
    return ['intake', 'checkin'].includes(args.type) ? [args.type] : null;
  };
  const getAppointmentPurpose = () => {
    if (args.type === 'admin') {
      return TimeAllocationPurpose.Admin;
    }
    if (args.type === 'timeOff') {
      return TimeAllocationPurpose.TimeOff;
    }
    return TimeAllocationPurpose.Availability;
  };
  return {
    startTime: args.startTime.toDate(),
    endTime: args.endTime.toDate(),
    organizationId: args.organizationId,
    childOrganizationIds: args.childOrganizationIds,
    purpose: getAppointmentPurpose(),
    appointmentTypes: getAppointmentTypes(),
    repeatsUntil: args.repeatsUntil ? args.repeatsUntil.toDate() : null,
    isFeeForServiceTime: args.isFeeForServiceTime,
    existingId: args?.existingId,
  };
};

export const hasSingleDayIntersection = (allocations: FriendlyAllocation[], date: Moment) => {
  return allocations.some(
    a => a.startTime.isAfter(date) && !a.weekly && a.startTime.day() === date.day()
  );
};

export const getSingleDayIntersections = (allocations: FriendlyAllocation[], date: Moment) => {
  return uniq(
    allocations
      .filter(i => i.startTime.isAfter(date) && !i.weekly && i.startTime.day() === date.day())
      .map(i => i.startTime.format('M/D/YY'))
  );
};

/**
 * Is the allocation slot within 3a to 8a ET
 * TODO: centralize logic on the backend: https://mantrahealthpde.atlassian.net/browse/PDE-142
 */
export const isWithinRestrictedTimeET = (slot: moment.Moment): boolean => {
  // Convert the slot to Eastern Time
  const slotET = slot.clone().tz('America/New_York');

  // Create moments for 3 AM and 8 AM on the same day in ET
  const startRestriction = slotET.clone().startOf('day').add(3, 'hours');
  const endRestriction = startRestriction.clone().add(5, 'hours');

  // Check if the slot is within the restricted time
  return slotET.isBetween(startRestriction, endRestriction, null, '()');
};

/**
 * Is the slot in the restricted start time range >=3am, <8am ET?
 * 3:00 AM is restricted as a startTime but valid as an endTime.
 */
export const isStartTimeRestrictedET = (slot: moment.Moment): boolean => {
  const etHour = slot.clone().tz('America/New_York').hour();
  return etHour >= 3 && etHour < 8;
};

/**
 * Returns a moment for 3:00 AM ET on the next calendar day after `forDate`.
 * This is the maximum "To" time for overnight allocations.
 */
export const getMaxEndTimeET = (forDate: Moment): Moment => {
  return forDate.clone().tz('America/New_York').startOf('day').add(1, 'day').add(3, 'hours');
};

export const getDateInTz = (m: Moment, timezone: TimeZone) => {
  return moment.tz({ date: m.date(), month: m.month(), year: m.year() }, timezone).startOf('day');
};

export const generateDropdownTimeSlots = ({
  timeType,
  isAdmin,
  forDate,
  allocation,
}: {
  timeType: TimeType;
  isAdmin: boolean;
  forDate: moment.Moment;
  allocation: LocalAllocation;
}): moment.Moment[] => {
  const dayStart = forDate.clone().startOf('day');

  // Same-day slots: 12:00 AM to 11:45 PM in display timezone
  const sameDaySlots = times(SLOTS_PER_DAY, i =>
    dayStart.clone().add(i * TIME_INTERVAL, 'minutes')
  );

  if (timeType === 'startTime') {
    // "From" dropdown: same-day slots only
    if (isAdmin) {
      return sameDaySlots;
    }
    // Provider: filter out slots in [3 AM, 8 AM) ET
    return sameDaySlots.filter(slot => !isStartTimeRestrictedET(slot));
  }

  // "To" (endTime) dropdown
  const hasStart = hasTimeType(allocation, 'startTime');
  if (!hasStart) {
    return sameDaySlots;
  }

  // startTime is selected — generate same-day + next-day overnight slots
  // Note: DST transitions may shift overnight durations by ±1 hour (moment-timezone handles the offset).
  const startTimeET = allocation.startTime.clone().tz('America/New_York');
  const isLateNightStart = !isAdmin && startTimeET.hour() < 3;

  // For providers starting before 3 AM ET, cap at 3 AM same day to avoid crossing the restricted window
  const maxEnd = isLateNightStart
    ? startTimeET.clone().startOf('day').add(3, 'hours')
    : getMaxEndTimeET(forDate);

  const nextDayStart = dayStart.clone().add(1, 'day');
  const nextDaySlots = times(NEXT_DAY_SLOTS, i =>
    nextDayStart.clone().add(i * TIME_INTERVAL, 'minutes')
  );

  return [...sameDaySlots, ...nextDaySlots].filter(
    slot => slot.isAfter(startTimeET) && !slot.isAfter(maxEnd)
  );
};

export const hasTimeType = <T extends TimeType>(
  allocation: LocalAllocation,
  timeType: T
): allocation is LocalAllocation & { [t in T]: Moment } => {
  return timeType in allocation && !isNil(allocation[timeType as keyof LocalAllocation]);
};

export const endTimeExceedsDay = (allocation: LocalAllocation, date: Moment, hours: number) => {
  const hasStart = hasTimeType(allocation, 'startTime');
  return (
    hasStart &&
    allocation.startTime
      .clone()
      .add(hours * 60 * 60, 'seconds')
      .isAfter(getMaxEndTimeET(date))
  );
};

export const startTimeExceedsDay = (allocation: LocalAllocation, date: Moment, hours: number) => {
  const hasEnd = hasTimeType(allocation, 'endTime');
  return (
    hasEnd &&
    allocation.endTime
      .clone()
      .subtract(hours * 60 * 60, 'seconds')
      .isBefore(date.clone().startOf('day'))
  );
};

export const startTimeRestrictedForProvider = ({
  isAdmin,
  allocation,
}: {
  isAdmin: boolean;
  allocation: LocalAllocation;
}): boolean => {
  // Admins can always edit
  if (isAdmin) {
    return false;
  }

  // If provider is editing an allocation with a starttime >= 3am < 8am ET disable it
  return hasTimeType(allocation, 'startTime') && isStartTimeRestrictedET(allocation.startTime);
};

export const endTimeRestrictedForProvider = ({
  isAdmin,
  allocation,
}: {
  isAdmin: boolean;
  allocation: LocalAllocation;
}): boolean => {
  // Admins can always edit
  if (isAdmin) {
    return false;
  }

  // If provider is editing an allocation with an endtime, check if it's in the restricted > 3am < 8am ET gap
  return hasTimeType(allocation, 'endTime') && isWithinRestrictedTimeET(allocation.endTime);
};
